import { useMemo, useSyncExternalStore } from "react"
import { createLabelColumn, createMetricColumns, createColumnGroupingModel } from "../helpers"
import { getLabelColumnDimensions, hasLabelGoToFinder } from "../helpers/create-label-column"
import { getFontLoadVersion, subscribeToFontLoading } from "../helpers/font-loading"
import { useOrderedMetricMetadata } from "./use-ordered-metric-metadata"

const isSameDimensions = (previous, next) => previous?.width === next.width && previous?.minWidth === next.minWidth

const createLabelDimensionsReader = (dimensionInputs) => {
  let measured = null
  return () => {
    const fontLoadVersion = getFontLoadVersion()
    if (measured?.fontLoadVersion !== fontLoadVersion) {
      const next = getLabelColumnDimensions(dimensionInputs)
      measured = { fontLoadVersion, dimensions: isSameDimensions(measured?.dimensions, next) ? measured.dimensions : next }
    }
    return measured.dimensions
  }
}

const useLabelDimensions = ({ idLabel, dropdownTableTitle, sortedContributorsArray, data, savedDataGridState, intradayPricePrefix, onGoToRow }) => {
  const hasGoToFinder = hasLabelGoToFinder({ onGoToRow, sortedContributorsArray })
  const readLabelDimensions = useMemo(
    () => createLabelDimensionsReader({ idLabel, dropdownTableTitle, sortedContributorsArray, data, savedDataGridState, intradayPricePrefix, hasGoToFinder }),
    [idLabel, dropdownTableTitle, sortedContributorsArray, data, savedDataGridState, intradayPricePrefix, hasGoToFinder],
  )

  return useSyncExternalStore(subscribeToFontLoading, readLabelDimensions)
}

export const useColumnDefinitions = (props) => {
  const { metricMetadata, metricCategories, sortMetricId, sortDirection, groupingEnabled, savedCustomColumnOrder, columnOrder, data } = props

  const labelDimensions = useLabelDimensions(props)

  // Reorder metricMetadata based on column order mode (default, custom, magnitude-based)
  const orderedMetricMetadata = useOrderedMetricMetadata(metricMetadata, columnOrder, data, savedCustomColumnOrder)

  const columnGroupingModel = useMemo(() => {
    return createColumnGroupingModel({ metricMetadata: orderedMetricMetadata, metricCategories, sortMetricId, sortDirection, groupingEnabled })
  }, [orderedMetricMetadata, metricCategories, sortMetricId, sortDirection, groupingEnabled])

  const groupEndFieldSet = useMemo(() => {
    const set = new Set()
    if (!columnGroupingModel) return set
    columnGroupingModel.forEach((item) => {
      if (item.children && Array.isArray(item.children) && item.children.length > 0) {
        const lastChild = item.children[item.children.length - 1]
        if (lastChild?.field) {
          set.add(lastChild.field)
        }
      }
    })
    return set
  }, [columnGroupingModel])

  const columns = useMemo(() => {
    const labelColumn = createLabelColumn({ ...props, labelDimensions })
    const metricColumns = createMetricColumns({ ...props, metricMetadata: orderedMetricMetadata, groupEndFieldSet })
    return [labelColumn, ...metricColumns]
  }, [props, labelDimensions, orderedMetricMetadata, groupEndFieldSet])

  return {
    columns,
    columnGroupingModel,
  }
}
